/**
 * MediationWall — Phase 7 M3. The external NEUTRAL's entire workspace.
 *
 * A mediator (org_type='mediator') Clerk org logs in and sees ONLY this: the
 * list of mediations it has been convened on, and — once BOTH parties have
 * consented — the allow-listed neutral view of the selected one. Staff/attorney
 * chrome and every case route are closed to it (M0 + the RoleRoute gate in
 * App.tsx). There is deliberately no "view full analysis" affordance.
 *
 * Contract: docs/plans/2026-10-11-phase7-mediation-neutral-view-contract.md
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  listMediations,
  getNeutralView,
  type MediationListItem,
  type MediationNeutralView as NeutralViewData,
} from '@/services/mediationApi';
import { MediationNeutralView } from '@/components/mediation/MediationNeutralView';

const MONO = 'font-mono tabular-nums';

function consentSummary(m: MediationListItem): string {
  const states = m.consents.map((c) => c.state);
  const granted = states.filter((s) => s === 'granted').length;
  return `${granted}/${states.length} consented`;
}

/** The gate 403s for any non-readable state (proposed/suspended/closed/cancelled,
 *  or active-but-a-consent-not-live); the copy must not conflate them. */
function gatedCopy(status?: string): string {
  switch (status) {
    case 'suspended':
      return 'This mediation is suspended — a party withdrew consent. The disclosed positions are hidden until consent is restored.';
    case 'closed':
      return 'This mediation is closed.';
    case 'cancelled':
      return 'This mediation was cancelled.';
    default:
      return 'Awaiting consent from both parties. The disclosed positions become visible once both sides have granted consent.';
  }
}

export default function MediationWall(): React.ReactElement {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();

  const [list, setList] = useState<MediationListItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [view, setView] = useState<NeutralViewData | null>(null);
  const [viewState, setViewState] = useState<'idle' | 'loading' | 'ready' | 'awaiting' | 'error'>('idle');

  useEffect(() => {
    let alive = true;
    listMediations()
      .then((r) => { if (alive) setList(r.mediations); })
      .catch(() => { if (alive) setListError('Could not load mediations.'); });
    return () => { alive = false; };
  }, []);

  const loadView = useCallback((id: string) => {
    setViewState('loading');
    setView(null);
    getNeutralView(id)
      .then((v) => { setView(v); setViewState('ready'); })
      .catch((e: unknown) => {
        // The backend gate 403s until the mediation is active (both consents
        // granted). That is the expected pre-active state, not an error screen.
        const status = (e as { status?: number } | null)?.status;
        setViewState(status === 403 ? 'awaiting' : 'error');
      });
  }, []);

  useEffect(() => {
    if (mediationId) loadView(mediationId);
    else { setView(null); setViewState('idle'); }
  }, [mediationId, loadView]);

  const selected = list?.find((m) => m.id === mediationId) || null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)', color: 'var(--color-text-primary)' }}>
      <div className="mx-auto" style={{ maxWidth: 1100, padding: '24px 16px' }}>
        <div className={`${MONO} uppercase`} style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--color-brand)' }}>
          Mediation — Neutral Workspace
        </div>

        <div className="mt-4 flex gap-6 flex-col md:flex-row">
          {/* Left: the neutral's mediations */}
          <aside style={{ width: '100%', maxWidth: 300 }}>
            <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Mediations
            </div>
            {listError && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{listError}</div>}
            {list && list.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No mediations convened yet.</div>
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {(list || []).map((m) => {
                const active = m.id === mediationId;
                return (
                  <li key={m.id}>
                    <button
                      onClick={() => navigate(`/mediation-wall/${m.id}`)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        background: active ? 'var(--color-bg-elevated)' : 'transparent',
                        border: '1px solid var(--color-border)',
                        borderLeftWidth: 3,
                        borderLeftColor: active ? 'var(--color-brand)' : 'transparent',
                        borderRadius: 2, padding: '10px 12px', marginBottom: 6, cursor: 'pointer',
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      <div style={{ fontSize: 13 }}>{m.title}</div>
                      <div className={MONO} style={{ fontSize: 9, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        {m.status} · {consentSummary(m)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Right: the selected mediation's neutral view */}
          <main style={{ flex: 1, minWidth: 0 }}>
            {viewState === 'idle' && (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', paddingTop: 40 }}>
                Select a mediation to view the disclosed positions.
              </div>
            )}
            {viewState === 'loading' && (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', paddingTop: 40 }}>Loading…</div>
            )}
            {viewState === 'awaiting' && (
              <div
                style={{
                  backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)',
                  borderRadius: 2, padding: 18, marginTop: 8,
                }}
              >
                <div style={{ fontSize: 14 }}>{selected?.title ?? 'Mediation'}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 6 }}>
                  {gatedCopy(selected?.status)}
                </div>
                {selected && (
                  <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 10 }}>
                    {consentSummary(selected)}
                  </div>
                )}
              </div>
            )}
            {viewState === 'error' && (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', paddingTop: 40 }}>
                This mediation is not available.
              </div>
            )}
            {viewState === 'ready' && view && <MediationNeutralView view={view} />}
          </main>
        </div>
      </div>
    </div>
  );
}
